/**
 * Resiliations — Liste globale des résiliations contrats (parité BE-TAYE).
 * Vue consolidée par contrat avec statut, date effet, envoi email/LRAR.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileX, Mail, Send, Truck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { en_cours: "En cours", annulee: "Annulée", effective: "Effective", none: "Aucune" };
const STATUS_STYLE = {
  en_cours: "bg-amber-100 text-amber-800 border-amber-300",
  annulee: "bg-slate-100 text-slate-600 border-slate-200",
  effective: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function Resiliations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [laposte, setLaposte] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [lrarOpen, setLrarOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: "", cc_emails: "", subject: "", intro_message: "" });
  const [lrarForm, setLrarForm] = useState({ recipient_name: "", recipient_address: "" });

  const load = async () => {
    try {
      const params = status !== "all" ? { status } : {};
      const r = await api.get("/resiliations", { params });
      setRows(r.data.resiliations || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); api.get("/laposte/status").then(r => setLaposte(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [status]);

  const filtered = rows.filter(r => {
    if (!q) return true; const s = q.toLowerCase();
    return (r.title || "").toLowerCase().includes(s)
      || (r.party_name || "").toLowerCase().includes(s)
      || (r.reference || "").toLowerCase().includes(s);
  });

  const sendEmail = async () => {
    if (!emailForm.to_email) { toast.error("Email destinataire requis"); return; }
    try {
      await api.post(`/contracts/${target.contract_id}/resiliation/send-email`, {
        to_email: emailForm.to_email,
        cc_emails: emailForm.cc_emails ? emailForm.cc_emails.split(",").map(s => s.trim()).filter(Boolean) : [],
        subject: emailForm.subject || undefined,
        intro_message: emailForm.intro_message || undefined,
      });
      toast.success("Email envoyé"); setEmailOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendLrar = async () => {
    if (!lrarForm.recipient_name || !lrarForm.recipient_address) {
      toast.error("Nom + adresse destinataire requis"); return;
    }
    try {
      const r = await api.post(`/contracts/${target.contract_id}/resiliation/send-lrar`, lrarForm);
      toast.success(`LRAR envoyée · ${r.data.tracking_ref}${r.data.mode === "simulation" ? " (SIMU)" : ""}`);
      setLrarOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="resiliations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Résiliations
          </h1>
          <p className="text-sm text-slate-500">
            {filtered.length} demande{filtered.length > 1 ? "s" : ""} · {rows.filter(r => r.resiliation_status === "en_cours").length} en cours
          </p>
        </div>
        {laposte && (
          <Badge variant="outline" className={laposte.mode === "simulation"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : "bg-emerald-50 text-emerald-700 border-emerald-200"}
            data-testid="laposte-badge">
            <Truck className="w-3 h-3 mr-1" />
            La Poste {laposte.mode === "simulation" ? "SIMULATION" : "LIVE"}
          </Badge>
        )}
      </div>

      <Card><CardContent className="p-4 flex flex-col md:flex-row gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher titre, partie, référence..." className="max-w-md"
          data-testid="resil-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="max-w-xs" data-testid="resil-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="annulee">Annulées</SelectItem>
            <SelectItem value="effective">Effectives</SelectItem>
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileX className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucune résiliation.</p>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">Contrat</TableHead>
              <TableHead className="text-xs uppercase">Partie</TableHead>
              <TableHead className="text-xs uppercase">Demandée le</TableHead>
              <TableHead className="text-xs uppercase">Effet</TableHead>
              <TableHead className="text-xs uppercase">Statut</TableHead>
              <TableHead className="text-xs uppercase">Email</TableHead>
              <TableHead className="text-xs uppercase">LRAR</TableHead>
              <TableHead className="text-xs uppercase text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r, i) => (
                <motion.tr key={r.contract_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(i, 15) * 0.015 }}
                  className="border-b hover:bg-slate-50" data-testid={`resil-row-${i}`}>
                  <TableCell>
                    <button className="text-sm font-medium text-slate-900 hover:text-blue-600 flex items-center gap-1"
                      onClick={() => navigate(`/app/contracts/${r.contract_id}`)}>
                      {r.title || "—"} <ExternalLink className="w-3 h-3" />
                    </button>
                    {r.reference && <div className="text-xs text-slate-500 font-mono">{r.reference}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{r.party_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.date_demande ? new Date(r.date_demande).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.date_effet ? new Date(r.date_effet).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_STYLE[r.resiliation_status] || ""}>{STATUS_LABEL[r.resiliation_status] || "—"}</Badge></TableCell>
                  <TableCell>{r.email_sent_at ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Mail className="w-3 h-3 mr-1" />Envoyé
                    </Badge>
                  ) : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell>{r.lrar_tracking_ref ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-mono text-[10px]">
                      {r.lrar_tracking_ref}
                    </Badge>
                  ) : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {r.resiliation_status === "en_cours" && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7"
                          onClick={() => { setTarget(r); setEmailForm({ to_email: "", cc_emails: "", subject: "", intro_message: "" }); setEmailOpen(true); }}
                          data-testid={`resil-email-${i}`}>
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7"
                          onClick={() => { setTarget(r); setLrarForm({ recipient_name: r.party_name || "", recipient_address: "" }); setLrarOpen(true); }}
                          data-testid={`resil-lrar-${i}`}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="resil-email-dialog">
          <DialogHeader><DialogTitle>Envoyer lettre par email</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Destinataire</Label>
              <Input value={emailForm.to_email} onChange={(e) => setEmailForm(p => ({ ...p, to_email: e.target.value }))}
                placeholder="contact@bailleur.fr" data-testid="resil-email-to" />
            </div>
            <div><Label>CC (séparés par virgule)</Label>
              <Input value={emailForm.cc_emails} onChange={(e) => setEmailForm(p => ({ ...p, cc_emails: e.target.value }))}
                placeholder="cc1@ex.fr, cc2@ex.fr" />
            </div>
            <div><Label>Objet (optionnel)</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm(p => ({ ...p, subject: e.target.value }))} />
            </div>
            <div><Label>Message d'introduction</Label>
              <Textarea rows={4} value={emailForm.intro_message} onChange={(e) => setEmailForm(p => ({ ...p, intro_message: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Annuler</Button>
            <Button onClick={sendEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="resil-email-send">
              <Mail className="w-4 h-4 mr-1.5" />Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lrarOpen} onOpenChange={setLrarOpen}>
        <DialogContent data-testid="resil-lrar-dialog">
          <DialogHeader><DialogTitle>Envoyer par LRAR (La Poste)</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          {laposte && laposte.mode === "simulation" && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              <b>Mode SIMULATION</b> — le partenariat La Poste n'est pas encore finalisé.
              Un tracking `SIMU-XXXXXX` sera généré à des fins de tests.
            </div>
          )}
          <div className="space-y-3">
            <div><Label>Nom destinataire</Label>
              <Input value={lrarForm.recipient_name} onChange={(e) => setLrarForm(p => ({ ...p, recipient_name: e.target.value }))}
                data-testid="resil-lrar-name" />
            </div>
            <div><Label>Adresse postale</Label>
              <Textarea rows={4} value={lrarForm.recipient_address} onChange={(e) => setLrarForm(p => ({ ...p, recipient_address: e.target.value }))}
                placeholder="123 rue de la Paix&#10;75001 Paris" data-testid="resil-lrar-address" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLrarOpen(false)}>Annuler</Button>
            <Button onClick={sendLrar} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="resil-lrar-send">
              <Send className="w-4 h-4 mr-1.5" />Envoyer LRAR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
